/**
 * Franchise storage utility functions for localStorage management
 * Handles complete franchise and salon data persistence
 */

// ============ Type Definitions ============

export interface FranchiseData {
  id: string;
  salon_id: string;
  owner_name: string;
  phone: string;
  type: string;
  name: string;
  description: string;
  address: string;
  domain: string;
  working_hours: Record<string, string>;
  customer_care_number: string;
  capacity: number;
  banner: {
    url: string;
    alt: string;
  };
  logo: string | { url: string; alt: string };
  features: string;
  template_id: string;
  payment_type: string;
  state: string;
  country: string;
  zipcode: string;
  timezone: string;
  lat: string;
  lng: string;
  account_id: string;
  package_details: {
    plan: string;
    features: string[];
    expiry: string;
  };
  is_enabled: string;
  is_voice_enabled: string;
  payment_perferences: {
    accepted_methods: string[];
    currency: string;
  };
  alternative_phone: string;
  created_by: string;
  updated_by: string | null;
  created_date: string;
  updated_date: string;
}

export interface SalonData {
  id: string;
  name: string;
  type: string;
  description: string;
  owner_name: string;
  state: string;
  country: string;
  zipcode: string;
  phone: string;
  alternative_phone: string;
  address: string;
  features: string;
  logo: string;
  account_id: string;
  domain: string;
  working_hours: Record<string, string>;
  customer_care_number: string;
  capacity: string;
  banner: {
    url: string;
    alt: string;
  };
  payment_type: string;
  template_id: string;
  package_details: {
    plan: string;
    features: string[];
    expiry: string;
  };
  is_enabled: string;
  is_voice_enabled: string;
  payment_perferences: {
    accepted_methods: string[];
    currency: string;
  };
}

export interface StorageData {
  franchise: FranchiseData;
  salon: SalonData;
  selectedAt: string; // ISO timestamp
}

// ============ Storage Key ============

const STORAGE_KEY = 'salon_franchise_data';

// ============ Main Functions ============

/**
 * Save complete franchise and salon data to localStorage
 * 
 * @param {StorageData} data - Complete franchise and salon data
 * 
 * @example
 * saveFranchiseData({
 *   franchise: franchiseObject,
 *   salon: salonObject,
 *   selectedAt: new Date().toISOString()
 * });
 */
export const saveFranchiseData = (data: StorageData): void => {
  try {
    const dataWithTimestamp = {
      ...data,
      selectedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataWithTimestamp));
  } catch (error) {
    console.error('Error saving franchise data to localStorage:', error);
  }
};

/**
 * Load complete franchise and salon data from localStorage
 * 
 * @returns {StorageData | null} Stored data or null if not found
 * 
 * @example
 * const data = loadFranchiseData();
 * if (data) {
 *   console.log(data.franchise.name);
 * }
 */
export const loadFranchiseData = (): StorageData | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error loading franchise data from localStorage:', error);
    return null;
  }
};

/**
 * Clear all franchise and salon data from localStorage
 */
export const clearFranchiseData = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing franchise data from localStorage:', error);
  }
};

/**
 * Check if franchise data exists in localStorage
 * 
 * @returns {boolean} True if data exists
 */
export const hasFranchiseData = (): boolean => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return !!data;
  } catch (error) {
    console.error('Error checking franchise data in localStorage:', error);
    return false;
  }
};

